import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import classNames from 'classnames';
import Header from '../../global/Header';
import Footer from '../../global/Footer';
import {
  isTrackedPath,
  isTrashieContainerPath,
  isFooterPath,
  getRewardsV2RedirectPath,
  isFooterExcludedPath,
} from '../../utils/routes';

import { EVENT_STREAM_APP_TYPES, EVENT_STREAM_EVENT_TYPES } from '../../config/service';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import LoadingBar from '../../global/LoadingBar';
import { getConfigurations, sendEventStream } from '../../utils/service';

import InformativeBanner from '../../global/InformativeBanner/InformativeBanner';
import { isMobile } from '../../../../hooks/useBreakPoint';
import { TRASHIE_BASE_URL, isProd } from '../../../../config/config';
import { useAlert } from '../../../providers/AlertProvider';
import { signOut } from '../../../../utils/auth';
import {
  ROUTE_DASHBOARD,
  ROUTE_LOGIN,
  ROUTE_LOGOUT,
  ROUTE_MY_ACCOUNT,
} from '../../config/routes';
import { setDocumentTitle } from '../../utils/utils';
import useGetSystemMessages from '../../utils/hooks/useGetSystemMessages';
import useRewardsV2Flag from '../../utils/hooks/useRewardsV2Flag';
import TrashieMyAccount from '../../views/MyAccount';
import { TrackingDetails as TrashieTrackingDetails } from '../../views/TrackingDetails';
import ACCOUNT_DRAWER_OPEN_EVENT, {
  ACCOUNT_DRAWER_STATE_EVENT,
  TRACKING_DRAWER_OPEN_EVENT,
  TRACKING_DRAWER_STATE_EVENT,
} from '../../config/events';
import { pushAccountDrawerViewedEvent } from '../../utils/gtm';

import Style from './TrashieContainer.module.scss';

const {
  main,
  content,
  hasMobileFooter,
  loader,
} = Style;

const TrashieContainer = ({ children }) => {
  const { pathname, search, hash } = useLocation();
  const history = useHistory();
  const { isLoading, isSignedIn, user, userData } = useFirebaseAuth();
  const rewardsV2Enabled = useRewardsV2Flag();
  const setAlert = useAlert();
  const isMobileSize = isMobile();

  const [isReady, setIsReady] = useState(false);
  const [isAccountDrawerMounted, setIsAccountDrawerMounted] = useState(false);
  const [isAccountDrawerVisible, setIsAccountDrawerVisible] = useState(false);
  const [isTrackingDrawerMounted, setIsTrackingDrawerMounted] = useState(false);
  const [isTrackingDrawerVisible, setIsTrackingDrawerVisible] = useState(false);
  const lastNonAccountLocationRef = useRef({
    pathname: ROUTE_DASHBOARD,
    search: '',
    hash: '',
  });
  const previousLocationRef = useRef({
    pathname,
    search,
    hash,
  });
  const skipNextRouteCloseRef = useRef(false);
  const closeAccountDrawerTimeoutRef = useRef(null);
  const closeTrackingDrawerTimeoutRef = useRef(null);

  const systemMessage = useGetSystemMessages();

  const isNotTrashieContainer = !isTrashieContainerPath(pathname) || typeof rewardsV2Enabled === 'undefined';

  const handleEventStream = useCallback(async () => {
    await sendEventStream({
      event: EVENT_STREAM_EVENT_TYPES.USER_LAST_VISIT,
      application: EVENT_STREAM_APP_TYPES.TRASHIE,
      data: {
        url: window.location.href,
      },
    });
  }, []);

  const validateNonProductionEnvWhitelistedUsers = useCallback(async () => {
    if (isProd()) {
      setIsReady(true);
      return;
    }

    if (window.location.hostname === 'localhost') {
      setIsReady(true);
      return;
    }

    const { value, error } = await getConfigurations('nonProductionEnvWhitelistedUsers');

    if (error) {
      setAlert({
        type: 'alert',
        message: error,
        error,
      });
      return;
    }

    if (!(value.emails || []).includes(user?.email) &&
      !(value.domains || []).some(domain => (user?.email || '').includes(domain))
    ) {
      await signOut();
      window.location.href = TRASHIE_BASE_URL.production;
      return;
    }

    setIsReady(true);
  }, [user]);

  useEffect(() => {
    if (isTrackedPath(pathname) && isSignedIn && userData) {
      handleEventStream(userData);
    }
  }, [isSignedIn, userData, pathname]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (isSignedIn) {
      validateNonProductionEnvWhitelistedUsers();
    } else {
      setIsReady(true);
    }
  }, [isLoading, isSignedIn]);

  useEffect(() => {
    setDocumentTitle();
  }, [pathname]);

  const openAccountDrawer = useCallback(() => {
    if (closeAccountDrawerTimeoutRef.current) {
      clearTimeout(closeAccountDrawerTimeoutRef.current);
      closeAccountDrawerTimeoutRef.current = null;
    }
    if (closeTrackingDrawerTimeoutRef.current) {
      clearTimeout(closeTrackingDrawerTimeoutRef.current);
      closeTrackingDrawerTimeoutRef.current = null;
    }
    setIsTrackingDrawerVisible(false);
    setIsTrackingDrawerMounted(false);

    setIsAccountDrawerMounted(true);
    requestAnimationFrame(() => {
      setIsAccountDrawerVisible(true);
    });
  }, []);

  const closeAccountDrawer = useCallback(() => {
    if (closeAccountDrawerTimeoutRef.current) {
      clearTimeout(closeAccountDrawerTimeoutRef.current);
      closeAccountDrawerTimeoutRef.current = null;
    }

    setIsAccountDrawerVisible(false);
    const closeDelay = window.matchMedia('(max-width: 767px)').matches ? 0 : 260;
    closeAccountDrawerTimeoutRef.current = setTimeout(() => {
      setIsAccountDrawerMounted(false);
      closeAccountDrawerTimeoutRef.current = null;
    }, closeDelay);
  }, []);

  const openTrackingDrawer = useCallback(() => {
    if (closeTrackingDrawerTimeoutRef.current) {
      clearTimeout(closeTrackingDrawerTimeoutRef.current);
      closeTrackingDrawerTimeoutRef.current = null;
    }
    if (closeAccountDrawerTimeoutRef.current) {
      clearTimeout(closeAccountDrawerTimeoutRef.current);
      closeAccountDrawerTimeoutRef.current = null;
    }
    setIsAccountDrawerVisible(false);
    setIsAccountDrawerMounted(false);

    setIsTrackingDrawerMounted(true);
    requestAnimationFrame(() => {
      setIsTrackingDrawerVisible(true);
    });
  }, []);

  const closeTrackingDrawer = useCallback(() => {
    if (closeTrackingDrawerTimeoutRef.current) {
      clearTimeout(closeTrackingDrawerTimeoutRef.current);
      closeTrackingDrawerTimeoutRef.current = null;
    }

    setIsTrackingDrawerVisible(false);
    const closeDelay = window.matchMedia('(max-width: 767px)').matches ? 0 : 260;
    closeTrackingDrawerTimeoutRef.current = setTimeout(() => {
      setIsTrackingDrawerMounted(false);
      closeTrackingDrawerTimeoutRef.current = null;
    }, closeDelay);
  }, []);

  useEffect(() => {
    const previousLocation = previousLocationRef.current;
    const didLocationChange = previousLocation.pathname !== pathname
      || previousLocation.search !== search
      || previousLocation.hash !== hash;

    if (didLocationChange) {
      if (pathname !== ROUTE_MY_ACCOUNT) {
        lastNonAccountLocationRef.current = { pathname, search, hash };
      }

      if (isTrackingDrawerVisible) {
        closeTrackingDrawer();
      }

      if (pathname !== ROUTE_MY_ACCOUNT && isAccountDrawerVisible) {
        if (skipNextRouteCloseRef.current) {
          skipNextRouteCloseRef.current = false;
        } else {
          closeAccountDrawer();
        }
      }
    }

    previousLocationRef.current = { pathname, search, hash };
  }, [
    pathname,
    search,
    hash,
    isAccountDrawerVisible,
    isTrackingDrawerVisible,
    closeAccountDrawer,
    closeTrackingDrawer,
  ]);

  useEffect(() => {
    if (pathname !== ROUTE_MY_ACCOUNT) {
      return;
    }

    const fallbackLocation = lastNonAccountLocationRef.current;
    const destinationPath = fallbackLocation.pathname || ROUTE_DASHBOARD;

    openAccountDrawer();
    skipNextRouteCloseRef.current = true;
    history.replace({
      pathname: destinationPath,
      search: fallbackLocation.search || '',
      hash: fallbackLocation.hash || '',
    });
  }, [pathname, history, openAccountDrawer]);

  useEffect(() => {
    const onOpenDrawer = () => {
      openAccountDrawer();
    };

    window.addEventListener(ACCOUNT_DRAWER_OPEN_EVENT, onOpenDrawer);
    return () => {
      window.removeEventListener(ACCOUNT_DRAWER_OPEN_EVENT, onOpenDrawer);
    };
  }, [openAccountDrawer]);

  useEffect(() => {
    const onOpenDrawer = () => {
      openTrackingDrawer();
    };

    window.addEventListener(TRACKING_DRAWER_OPEN_EVENT, onOpenDrawer);
    return () => {
      window.removeEventListener(TRACKING_DRAWER_OPEN_EVENT, onOpenDrawer);
    };
  }, [openTrackingDrawer]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent(ACCOUNT_DRAWER_STATE_EVENT, {
      detail: { isOpen: isAccountDrawerVisible },
    }));
  }, [isAccountDrawerVisible]);

  useEffect(() => {
    if (!isAccountDrawerVisible) {
      return;
    }
    pushAccountDrawerViewedEvent();
  }, [isAccountDrawerVisible]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent(TRACKING_DRAWER_STATE_EVENT, {
      detail: { isOpen: isTrackingDrawerVisible },
    }));
  }, [isTrackingDrawerVisible]);

  useEffect(() => {
    if (!isNotTrashieContainer && isSignedIn && user && !user.emailVerified) {
      history.replace(ROUTE_LOGIN);
    }
  }, [isNotTrashieContainer, isLoading, isSignedIn, user]);

  useEffect(() => {
    if (!isNotTrashieContainer && isSignedIn && userData?.forcePwdReset) {
      history.replace(ROUTE_LOGOUT);
    }
  }, [isNotTrashieContainer, isSignedIn, userData]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    if (isAccountDrawerMounted || isTrackingDrawerMounted) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isAccountDrawerMounted, isTrackingDrawerMounted]);

  useEffect(() => () => {
    if (closeAccountDrawerTimeoutRef.current) {
      clearTimeout(closeAccountDrawerTimeoutRef.current);
    }
    if (closeTrackingDrawerTimeoutRef.current) {
      clearTimeout(closeTrackingDrawerTimeoutRef.current);
    }
  }, []);

  if (rewardsV2Enabled === true) {
    const rewardsV2RedirectPath = getRewardsV2RedirectPath({ pathname, search, hash });

    if (rewardsV2RedirectPath) {
      history.replace(rewardsV2RedirectPath);
      return null;
    }
  }

  if (isNotTrashieContainer) {
    return null;
  }

  return (
    <div className={main}>
      <Header isBannerVisible={!!systemMessage} />
      <div className={classNames(content, {
        [hasMobileFooter]: isMobileSize && isFooterPath(pathname),
        [Style.noTopOffset]: isMobileSize && pathname === ROUTE_MY_ACCOUNT,
      })}
      >
        {isLoading || !isReady ? (
          <div className={loader}>
            <LoadingBar />
          </div>
        ) : (
          <>
            {systemMessage && (
              <InformativeBanner
                isNotification
                mainText={systemMessage}
              />
            )}
            {children}
          </>
        )}
      </div>
      {((isMobileSize &&
        !isFooterPath(pathname)) ||
        isFooterExcludedPath(pathname)) ? null : <Footer />}
      {isAccountDrawerMounted && (
        <div
          className={classNames(Style.accountDrawerLayer, {
            [Style.accountDrawerLayerVisible]: isAccountDrawerVisible,
          })}
          role="presentation"
        >
          <button
            type="button"
            aria-label="Close account drawer"
            className={Style.accountDrawerBackdrop}
            onClick={closeAccountDrawer}
          />
          <div className={classNames(Style.accountDrawerPanel, {
            [Style.accountDrawerPanelVisible]: isAccountDrawerVisible,
          })}
          >
            <TrashieMyAccount isDrawer onClose={closeAccountDrawer} />
          </div>
        </div>
      )}
      {isTrackingDrawerMounted && (
        <div
          className={classNames(Style.trackingDrawerLayer, {
            [Style.trackingDrawerLayerVisible]: isTrackingDrawerVisible,
          })}
          role="presentation"
        >
          <button
            type="button"
            aria-label="Close tracking drawer"
            className={Style.accountDrawerBackdrop}
            onClick={closeTrackingDrawer}
          />
          <div className={classNames(Style.trackingDrawerPanel, {
            [Style.trackingDrawerPanelVisible]: isTrackingDrawerVisible,
          })}
          >
            <TrashieTrackingDetails isDrawer />
          </div>
        </div>
      )}
    </div>
  );
};

export default TrashieContainer;
